import { useState, useContext, useEffect } from "react"
import { useNavigate, Link } from 'react-router-dom'
import { Container, Row, Col, Form, Button, Carousel, Spinner } from "react-bootstrap";
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff } from "react-icons/fi";
import "./login.css"
import { UserContext } from '../../components/states/contexts/UserContext';
import ApiService from '../../helpers/ApiServices';
import { TokenService } from '../../helpers/StorageServices';

// Fixed: Structured to match your rendering layout exactly (using objects with 'description')
const initialDataSet = {
    points: [
        { color: "#F3C052", title: "Making Manufacturing Technologies Flexible", description: "Integrate multiple systems and minimize existing system limitations." },
        { color: "#4CB2A8", title: "Custom Solutions at Small Cost", description: "Provide any size manufacturer custom solutions to solve unique problems at a small cost." },
        { color: "#6E8B4E", title: "Adapt Software to Your Business", description: "Change the software, not the business to solve your business challenges." },
        { color: "#9EBA49", title: "Prioritize Impactful Investments", description: "Prioritize software investments to more impactful areas of the business." },
        { color: "#6FA850", title: "Extend ROI and Reduce TCO", description: "Use current systems with Flex, extending return on investment and decreasing total cost of ownership." },
        { color: "#4BA3A5", title: "Bridge System Gaps", description: "Bridge the gap between disparate systems, erasing unnecessary data silos." }
    ],
    announcements: [
        { description: "Partnering with Purdue Manufacturing Extensions to help Indiana Manufacturing Companies." },
        { description: "Collaborating with Purdue University on build technology on IIoT connections." },
        { description: "State of the art Production Scheduling is coming." },
        { description: "AI powered FlexAnalytics is on the way." }
    ]
}


const LoginPage = () => {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const [formError, setFormError] = useState("");

    const [dataSet, setDataSet] = useState(initialDataSet);

    const { dispatch } = useContext(UserContext);
    const navigate = useNavigate();

    const handleLoginSubmit = async (e) => {
        e.preventDefault()
        setIsLoading(true)
        setFormError("")

        try {
            dispatch({ type: "LOGIN_START" });
            const response = await ApiService.post('accessControl/login', {
                email, password
            });
            if (response.data.isSuccess) {
                TokenService.saveToken(response.data.token)
                ApiService.setHeader();

                if (response?.data.document.giveAccess) {
                    dispatch({ type: "LOGIN_SUCCESS", payload: response.data.document });
                    navigate('/accounts')
                } else {
                    setFormError("This user does not have the access to login. Please contact the administrator");
                }
            } else {
                dispatch({ type: "LOGIN_FAILURE" });
            }
        } catch (err) {
            setFormError("Your email or password is incorrect! Please try again.")
            dispatch({ type: "LOGIN_FAILURE" });
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const fetchMarketingData = async () => {
            try {
                const [eventResponse, highlightResponse] = await Promise.all([
                    ApiService.get("event"),
                    ApiService.get("highlight")
                ]);

                // Background update: only overrides if data safely exists
                setDataSet(prev => ({
                    ...prev,
                    points: eventResponse?.data?.isSuccess && eventResponse.data?.documents?.length > 0
                        ? eventResponse.data.documents
                        : prev.points,
                    announcements: highlightResponse?.data?.isSuccess && highlightResponse.data?.documents?.length > 0
                        ? highlightResponse.data.documents
                        : prev.announcements
                }));
            } catch (err) {
                console.error("Failed to silently load marketing data:", err);
            }
        };

        fetchMarketingData();
    }, []);

    return (
        <div className="login-viewport">
            <Container fluid className="g-0 h-100">
                <Row className="g-0 h-100">
                    {/* Left Form Panel */}
                    <Col lg={5} xl={4} className="form-panel-section">
                        <div className="form-panel-wrapper">
                            <div>
                                <div className="brand-header">
                                    <img
                                        src="/static/pctflex.png"
                                        alt="PCT Flex"
                                        className="brand-logo-img"
                                    />
                                </div>

                                <div className="login-meta">
                                    <h1 className="welcome-title">Welcome Back</h1>
                                    {/* <p className="welcome-subtitle">Making Manufacturing Technologies Flexible</p> */}
                                </div>

                                <Form onSubmit={handleLoginSubmit} className="interactive-form">
                                    {formError && (
                                        <div className="form-alert-banner animate-fade-in">
                                            <span>{formError}</span>
                                        </div>
                                    )}

                                    <Form.Group className="custom-input-group">
                                        <Form.Label className="field-label">Email Address</Form.Label>
                                        <div className="input-with-icon">
                                            <FiMail className="input-addon-icon" />
                                            <Form.Control
                                                type="email"
                                                placeholder="Email Address"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                disabled={isLoading}
                                                className="custom-form-input"
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="custom-input-group">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Form.Label className="field-label">Password</Form.Label>
                                            <a target="_blank" rel="noreferrer" href="https://auth.pctflex.com/forgotpassword" className="form-link-action">Forgot password?</a>
                                        </div>
                                        <div className="input-with-icon">
                                            <FiLock className="input-addon-icon" />
                                            <Form.Control
                                                type={showPassword ? "text" : "password"}
                                                placeholder="••••••••"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                disabled={isLoading}
                                                className="custom-form-input password-field-padding"
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-reveal-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={isLoading}
                                                title={showPassword ? "Hide password" : "Show password"}
                                            >
                                                {showPassword ? <FiEyeOff /> : <FiEye />}
                                            </button>
                                        </div>
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        className="m-0 primary-action-btn w-100"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Authenticating...
                                            </>
                                        ) : (
                                            <>
                                                Log In <FiArrowRight className="ms-2" />
                                            </>
                                        )}
                                    </Button>
                                </Form>
                            </div>

                            <div className="form-panel-footer">
                                <p>© {new Date().getFullYear()} PCT Flex Platform. All rights reserved.</p>
                            </div>
                        </div>
                    </Col>

                    {/* Right Marketing/Info Panel */}
                    <Col lg={7} xl={8} className="showcase-panel-section d-none d-lg-flex">
                        <div className="showcase-overlay"></div>
                        <div className="showcase-content-container">

                            <div>
                                <h1 className="showcase-main-title">Making Manufacturing Technologies Flexible</h1>
                            </div>

                            <div className="showcase-glass-card">
                                <Carousel indicators={true} controls={false} interval={5000} className="showcase-carousel">
                                    {dataSet?.points?.map((point, index) => (
                                        <Carousel.Item key={index}>
                                            <div className="carousel-slide-inner">
                                                <span className="carousel-badge" style={{ backgroundColor: point?.color }}>Platform Value</span>
                                                <h2 className="slide-headline">{point?.title}</h2>
                                                <p className="slide-description">{point?.description}</p>
                                            </div>
                                        </Carousel.Item>
                                    ))}
                                </Carousel>
                            </div>

                            <div className="showcase-announcements-card">
                                <h3 className="annments-card-title">Latest Announcements</h3>
                                <ul className="annments-clean-list">
                                    {dataSet?.announcements?.map((item, idx) => (
                                        <li key={idx} className="annment-item">
                                            <div className="annment-bullet"></div>
                                            <p className="annment-text">{item?.description}</p>
                                        </li>
                                    ))}
                                </ul>
                                <div className="pt-3">
                                    <Button as={Link} to="https://www.paapri.com/free-consultation/" variant="outline-light" className="learn-more-btn">
                                        Learn More
                                    </Button>
                                </div>
                            </div>

                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default LoginPage